import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getAuthors } from '../services/author_service'
import {
  computeRoyalty,
  deleteRoyalty,
  getRoyalties,
  getRoyaltyBookOptions,
  payRoyalty,
} from '../services/royalty_service'
import './Royalty.css'

const emptyQuery = { author: '', fromDate: '', toDate: '' }
const emptyForm = {
  withholdingTax: '',
  authorid: '',
  bookid: '',
  fromDate: '',
  toDate: '',
  comment: '',
}
const emptyPaymentForm = { paymentDate: '', amount: '', comment: '' }

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getAuthorId = (author) =>
  getValue(author, ['authorid', 'author_id', 'id'], '')
const getAuthorName = (author) =>
  getValue(author, ['author_name', 'authorName', 'name', 'fullname'])
const getBookId = (book) => getValue(book, ['bookid', 'book_id', 'id'], '')
const getBookAuthorIds = (book) => getValue(book, ['authorids'], [])
const getBookTitle = (book) =>
  getValue(book, ['book_title', 'bookTitle', 'title', 'name'])
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)
const getRoyaltyTotal = (royalty) =>
  Number(getValue(royalty, ['total', 'amount'], 0)) || 0
const getWithholdingAmount = (royalty) =>
  Number.isFinite(Number(getValue(royalty, ['withholding_amount'], NaN)))
    ? Number(getValue(royalty, ['withholding_amount'], 0))
    : getRoyaltyTotal(royalty) *
      ((Number(getValue(royalty, ['withholding_tax'], 0)) || 0) / 100)
const getPayableAmount = (royalty) =>
  Number.isFinite(Number(getValue(royalty, ['amount_payable'], NaN)))
    ? Number(getValue(royalty, ['amount_payable'], 0))
    : getRoyaltyTotal(royalty) - getWithholdingAmount(royalty)
const getRoyaltyStatus = (royalty) => {
  const preferredKeys = [
    'royalty_status',
    'royaltyStatus',
    'royaltystatus',
    'payment_status',
    'paymentStatus',
    'paymentstatus',
    'status',
    'status_name',
    'statusName',
  ]
  const statusKeys = Object.keys(royalty || {}).filter((key) =>
    /status/i.test(key),
  )
  const keys = [...new Set([...preferredKeys, ...statusKeys])]

  const findStatusValue = (value) => {
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value).trim()
    }
    if (Array.isArray(value)) {
      return value.map(findStatusValue).find(Boolean) || ''
    }
    if (value && typeof value === 'object') {
      for (const key of ['name', 'label', 'status', 'value']) {
        const nestedValue = findStatusValue(value[key])
        if (nestedValue) return nestedValue
      }
    }
    return ''
  }

  return (
    keys.map((key) => findStatusValue(royalty?.[key])).find(Boolean) || ''
  )
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
}
const formatRoyaltyStatus = (status) =>
  status
    ? status
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    : '-'

function Royalty() {
  const { user, hasPermission } = useAuth()
  const canAddRoyalty = hasPermission('addRoyalty')
  const canViewRoyalty =
    hasPermission('viewRoyalty') || hasPermission('viewRoyalties')
  const canDeleteRoyalty = hasPermission('deleteRoyalty')
  const loginUserId = getValue(user, ['userid', 'user_id', 'id'], '')
  const [query, setQuery] = useState(emptyQuery)
  const [appliedQuery, setAppliedQuery] = useState(emptyQuery)
  const [royalties, setRoyalties] = useState([])
  const [authors, setAuthors] = useState([])
  const [books, setBooks] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [nextPage, setNextPage] = useState(null)
  const [currentCursor, setCurrentCursor] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [cursorHistory, setCursorHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [isQueryOpen, setIsQueryOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [formError, setFormError] = useState('')
  const [isComputing, setIsComputing] = useState(false)
  const [viewingRoyalty, setViewingRoyalty] = useState(null)
  const [payingRoyalty, setPayingRoyalty] = useState(null)
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm)
  const [paymentError, setPaymentError] = useState('')
  const [isPaying, setIsPaying] = useState(false)
  const [royaltyToDelete, setRoyaltyToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const clearPrintedStatement = () => setViewingRoyalty(null)
    window.addEventListener('afterprint', clearPrintedStatement)

    return () => window.removeEventListener('afterprint', clearPrintedStatement)
  }, [])

  const loadRoyalties = async (
    cursor = null,
    page = 1,
    filters = appliedQuery,
  ) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getRoyalties({ cursor, page, ...filters })
      setRoyalties(result.royalties)
      setNextCursor(result.nextCursor)
      setNextPage(result.nextPage)
      setCurrentCursor(cursor)
      setCurrentPage(result.currentPage || page)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    Promise.all([getAuthors({ page: 1 }), getRoyaltyBookOptions()])
      .then(([authorResult, loadedBooks]) => {
        setAuthors(authorResult.authors)
        setBooks(loadedBooks)
      })
      .catch((requestError) => setError(requestError.message))
    loadRoyalties(null, 1, emptyQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateQuery = (event) => {
    const { name, value } = event.target
    setQuery((current) => ({ ...current, [name]: value }))
  }
  const searchRoyalties = (event) => {
    event.preventDefault()
    setAppliedQuery(query)
    setCursorHistory([])
    loadRoyalties(null, 1, query)
  }
  const clearQuery = () => {
    setQuery(emptyQuery)
    setAppliedQuery(emptyQuery)
    setCursorHistory([])
    loadRoyalties(null, 1, emptyQuery)
  }
  const goToNextPage = () => {
    if (!nextCursor && !nextPage) return
    setCursorHistory((history) => [
      ...history,
      { cursor: currentCursor, page: currentPage },
    ])
    loadRoyalties(nextCursor, nextPage || currentPage, appliedQuery)
  }
  const goToPreviousPage = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previous = history.pop()
    setCursorHistory(history)
    loadRoyalties(previous.cursor, previous.page, appliedQuery)
  }
  const openComputeModal = () => {
    setForm(emptyForm)
    setFormError('')
    setIsModalOpen(true)
  }
  const closeComputeModal = () => {
    if (!isComputing) setIsModalOpen(false)
  }
  const updateForm = (event) => {
    const { name, value } = event.target
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'authorid' ? { bookid: '' } : {}),
    }))
  }
  const availableBooks = books.filter((book) =>
    getBookAuthorIds(book).some(
      (authorId) => String(authorId) === String(form.authorid),
    ),
  )
  const submitCompute = async (event) => {
    event.preventDefault()
    setIsComputing(true)
    setFormError('')
    try {
      await computeRoyalty({
        withholding_tax: Number(form.withholdingTax),
        authorid: form.authorid,
        bookid: form.bookid,
        from_date: form.fromDate,
        to_date: form.toDate,
        comment: form.comment.trim(),
        userid: loginUserId,
      })
      setIsModalOpen(false)
      await loadRoyalties(currentCursor, currentPage, appliedQuery)
    } catch (requestError) {
      setFormError(requestError.message)
    } finally {
      setIsComputing(false)
    }
  }

  const confirmDelete = async () => {
    if (!royaltyToDelete) return
    const royaltyId = getValue(
      royaltyToDelete,
      ['royaltyid', 'royalty_id', 'id'],
      '',
    )

    setIsDeleting(true)
    setError('')
    try {
      await deleteRoyalty(royaltyId)
      setRoyaltyToDelete(null)
      await loadRoyalties(currentCursor, currentPage, appliedQuery)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsDeleting(false)
    }
  }

  const printRoyalty = (royalty) => {
    setViewingRoyalty(royalty)
    window.setTimeout(() => window.print(), 0)
  }

  const openRoyaltyPayment = (royalty) => {
    setPayingRoyalty(royalty)
    setPaymentForm({
      paymentDate: new Date().toISOString().slice(0, 10),
      amount: getPayableAmount(royalty).toFixed(2),
      comment: '',
    })
    setPaymentError('')
  }
  const closeRoyaltyPayment = () => {
    if (!isPaying) setPayingRoyalty(null)
  }
  const updatePaymentForm = (event) => {
    const { name, value } = event.target
    setPaymentForm((current) => ({ ...current, [name]: value }))
  }
  const submitRoyaltyPayment = async (event) => {
    event.preventDefault()
    if (!payingRoyalty) return

    const amount = Number(paymentForm.amount)
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentError('Enter a payment amount greater than zero.')
      return
    }

    setIsPaying(true)
    setPaymentError('')
    try {
      await payRoyalty({
        royaltyid: getValue(
          payingRoyalty,
          ['royaltyid', 'royalty_id', 'id'],
          '',
        ),
        authorid: getValue(payingRoyalty, ['authorid', 'author_id'], ''),
        pay_by: loginUserId,
        payment_date: paymentForm.paymentDate,
        amount,
        comment: paymentForm.comment.trim(),
      })
      setPayingRoyalty(null)
      await loadRoyalties(currentCursor, currentPage, appliedQuery)
    } catch (requestError) {
      setPaymentError(requestError.message)
    } finally {
      setIsPaying(false)
    }
  }

  return (
    <div className='royalty-page'>
      <nav className='royalty-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Royalties</strong>
      </nav>
      <header className='royalty-page__header'>
        <div>
          <span className='app-content__eyebrow'>Finance</span>
          <h1>Royalties</h1>
          <p>Review computed royalties and prepare a new author statement.</p>
        </div>
        <div className='royalty-page__header-actions'>
          <Link className='royalty-button' to='/admin/royalties/payments'>
            Royalty payments
          </Link>
          {canAddRoyalty && (
            <button
              className='royalty-button royalty-button--primary'
              type='button'
              onClick={openComputeModal}
            >
              + Compute royalty
            </button>
          )}
        </div>
      </header>
      {error && (
        <div className='royalty-page__error' role='alert'>
          {error}
        </div>
      )}

      <section className='royalty-query-card'>
        <button
          className='royalty-query-card__toggle'
          type='button'
          onClick={() => setIsQueryOpen((open) => !open)}
          aria-expanded={isQueryOpen}
        >
          <span>
            <span className='app-content__eyebrow'>Refine results</span>
            <strong>Royalty query</strong>
          </span>
          <span aria-hidden='true'>{isQueryOpen ? '−' : '+'}</span>
        </button>
        {isQueryOpen && (
          <form className='royalty-query-form' onSubmit={searchRoyalties}>
            <label>
              Author
              <select name='author' value={query.author} onChange={updateQuery}>
                <option value=''>All authors</option>
                {authors.map((author) => (
                  <option key={getAuthorId(author)} value={getAuthorId(author)}>
                    {getAuthorName(author)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              From date
              <input
                name='fromDate'
                type='date'
                value={query.fromDate}
                onChange={updateQuery}
              />
            </label>
            <label>
              To date
              <input
                name='toDate'
                type='date'
                value={query.toDate}
                onChange={updateQuery}
              />
            </label>
            <div className='royalty-query-form__actions'>
              <button
                className='royalty-button royalty-button--primary'
                type='submit'
              >
                Search
              </button>
              <button
                className='royalty-button'
                type='button'
                onClick={clearQuery}
              >
                Clear
              </button>
            </div>
          </form>
        )}
      </section>

      <section
        className='royalty-table-card'
        aria-labelledby='royalty-table-title'
      >
        <div className='royalty-table-card__header'>
          <div>
            <span className='app-content__eyebrow'>Royalty register</span>
            <h2 id='royalty-table-title'>Computed royalties</h2>
            <p>{royalties.length} royalties on this page</p>
          </div>
        </div>
        <div className='royalty-table-wrap'>
          <table className='royalty-table'>
            <thead>
              <tr>
                <th>Date computed</th>
                <th>Author</th>
                <th>Computed by</th>
                <th>Total</th>
                <th>Period</th>
                <th>Status</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='royalty-table__empty' colSpan='7'>
                    Loading royalties...
                  </td>
                </tr>
              ) : royalties.length ? (
                royalties.map((royalty, index) => (
                  <tr
                    key={`${getValue(royalty, ['royaltyid', 'royalty_id', 'id'], index)}-${index}`}
                  >
                    {(() => {
                      const royaltyStatus = getRoyaltyStatus(royalty)
                      const canPay = ['not_paid', 'part_payment'].includes(
                        royaltyStatus,
                      )
                      const canDelete = ![
                        'fully_paid',
                        'part_payment',
                      ].includes(royaltyStatus)

                      return (
                        <>
                          <td>
                            {formatDate(
                              getValue(royalty, [
                                'date_computed',
                                'computed_date',
                                'created_at',
                              ]),
                            )}
                          </td>
                          <td>
                            <strong>
                              {getValue(royalty, ['author_name', 'author'])}
                            </strong>
                          </td>
                          <td>
                            {getValue(royalty, [
                              'computed_by',
                              'computed_by_user',
                              'user_fullname',
                            ])}
                          </td>
                          <td className='royalty-table__amount'>
                            {formatCurrency(
                              getValue(
                                royalty,
                                ['total', 'total_amount', 'amount'],
                                0,
                              ),
                            )}
                          </td>
                          <td>
                            {formatDate(
                              getValue(royalty, ['from_date', 'period_from']),
                            )}{' '}
                            /{' '}
                            {formatDate(
                              getValue(royalty, ['to_date', 'period_to']),
                            )}
                          </td>
                          <td>{formatRoyaltyStatus(royaltyStatus)}</td>
                          <td>
                            <div className='royalty-actions'>
                              {canPay && canViewRoyalty && (
                                <button
                                  type='button'
                                  onClick={() => openRoyaltyPayment(royalty)}
                                >
                                  Pay
                                </button>
                              )}
                              <button
                                type='button'
                                onClick={() => printRoyalty(royalty)}
                              >
                                Print
                              </button>
                              {canDelete && canDeleteRoyalty && (
                                <button
                                  className='royalty-actions__delete'
                                  type='button'
                                  onClick={() => setRoyaltyToDelete(royalty)}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )
                    })()}
                  </tr>
                ))
              ) : (
                <tr>
                  <td className='royalty-table__empty' colSpan='7'>
                    No royalties match the selected query.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='royalty-pagination'>
          <span>Showing up to 20 royalties</span>
          <div>
            <button
              type='button'
              onClick={goToPreviousPage}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextPage}
              disabled={(!nextCursor && !nextPage) || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {viewingRoyalty && (
        <div
          className='royalty-modal__backdrop royalty-view-modal'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && setViewingRoyalty(null)
          }
        >
          <section
            className='royalty-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='royalty-view-title'
          >
            <div className='royalty-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Royalty statement</span>
                <h2 id='royalty-view-title'>Royalty computation</h2>
              </div>
              <button
                type='button'
                onClick={() => setViewingRoyalty(null)}
                aria-label='Close royalty details'
              >
                x
              </button>
            </div>
            <dl className='royalty-view-details'>
              <div>
                <dt>Company</dt>
                <dd>Buck Press Limited</dd>
              </div>
              <div>
                <dt>Computation ID</dt>
                <dd>{getValue(viewingRoyalty, ['royaltyid', 'id'])}</dd>
              </div>
              <div>
                <dt>Author</dt>
                <dd>{getValue(viewingRoyalty, ['author_name', 'author'])}</dd>
              </div>
              <div>
                <dt>Contact</dt>
                <dd>{getValue(viewingRoyalty, ['author_contact'])}</dd>
              </div>
              <div>
                <dt>Address</dt>
                <dd>{getValue(viewingRoyalty, ['author_address'])}</dd>
              </div>
              <div>
                <dt>Initiated by</dt>
                <dd>
                  {getValue(viewingRoyalty, [
                    'computed_by',
                    'computed_by_user',
                  ])}
                </dd>
              </div>
              <div>
                <dt>Date computed</dt>
                <dd>
                  {formatDate(
                    getValue(viewingRoyalty, [
                      'date_computed',
                      'computed_date',
                    ]),
                  )}
                </dd>
              </div>
              <div>
                <dt>Royalty total</dt>
                <dd>{formatCurrency(getRoyaltyTotal(viewingRoyalty))}</dd>
              </div>
              <div>
                <dt>Withholding tax</dt>
                <dd>
                  {getValue(viewingRoyalty, ['withholding_tax'], 0)}% (
                  {formatCurrency(getWithholdingAmount(viewingRoyalty))})
                </dd>
              </div>
              <div>
                <dt>Payable</dt>
                <dd>{formatCurrency(getPayableAmount(viewingRoyalty))}</dd>
              </div>
              <div>
                <dt>Date interval</dt>
                <dd>
                  From: {formatDate(viewingRoyalty.from_date)} To:{' '}
                  {formatDate(viewingRoyalty.to_date)}
                </dd>
              </div>
            </dl>
            <div className='royalty-view-items'>
              <h3>Books</h3>
              {viewingRoyalty.items?.length ? (
                viewingRoyalty.items.map((item, index) => (
                  <div
                    className='royalty-view-item'
                    key={`${item.book_title}-${index}`}
                  >
                    <strong>{item.book_title}</strong>
                    <span>
                      {item.quantity} copies | Invoice:{' '}
                      {formatCurrency(item.invoice_value)} |{' '}
                      {item.royalty_percent}% ={' '}
                      {formatCurrency(item.royalty_amount)}
                    </span>
                  </div>
                ))
              ) : (
                <p>No book details available.</p>
              )}
            </div>
            <p className='royalty-view-comment'>
              {getValue(viewingRoyalty, ['comment'], 'No comment')}
            </p>
          </section>
        </div>
      )}

      {viewingRoyalty && (
        <div className='royalty-print-sheet' aria-hidden='true'>
          <header className='royalty-print-sheet__header'>
            <h1>Buck Press Limited</h1>
            <span>
              Date Computed: {formatDate(viewingRoyalty.date_computed)}
            </span>
          </header>
          <div className='royalty-print-sheet__company'>
            <div>
              <strong>Buck Press Limited</strong>
              <br />
              Publishing Department
              <br />
              Accra - Nsawam Highway, Ofankor
              <br />
              P.O. Box AN 12321, Accra-Ghana
              <br />
              Tel: (233) 302-408061
              <br />
              Email: publishing@buckpress.com
            </div>
            <div>
              ID# {getValue(viewingRoyalty, ['royaltyid', 'id'])}
              <br />
              Initiated by{' '}
              <strong>
                {getValue(viewingRoyalty, ['computed_by', 'computed_by_user'])}
              </strong>
            </div>
          </div>
          <p>
            <strong>Name of Author:</strong>{' '}
            {getValue(viewingRoyalty, ['author_name', 'author'])}
            <br />
            <strong>Contact:</strong>{' '}
            {getValue(viewingRoyalty, ['author_contact'])}
            <br />
            <strong>Address:</strong>{' '}
            {getValue(viewingRoyalty, ['author_address'])}
          </p>
          <table>
            <thead>
              <tr>
                <th>Book</th>
                <th>No. of Books</th>
                <th>Invoice Value</th>
                <th>Royalty %</th>
                <th>Royalty Value</th>
              </tr>
            </thead>
            <tbody>
              {(viewingRoyalty.items || []).map((item, index) => (
                <tr key={index}>
                  <td>{item.book_title}</td>
                  <td>{item.quantity}</td>
                  <td>{formatCurrency(item.invoice_value)}</td>
                  <td>{item.royalty_percent}%</td>
                  <td>{formatCurrency(item.royalty_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <dl className='royalty-print-sheet__totals'>
            <div>
              <dt>Total:</dt>
              <dd>{formatCurrency(getRoyaltyTotal(viewingRoyalty))}</dd>
            </div>
            <div>
              <dt>Withholding Tax:</dt>
              <dd>{formatCurrency(getWithholdingAmount(viewingRoyalty))}</dd>
            </div>
            <div>
              <dt>Payable:</dt>
              <dd>{formatCurrency(getPayableAmount(viewingRoyalty))}</dd>
            </div>
            <div>
              <dt>Date interval:</dt>
              <dd>
                From: {formatDate(viewingRoyalty.from_date)} To:{' '}
                {formatDate(viewingRoyalty.to_date)}
              </dd>
            </div>
          </dl>
        </div>
      )}

      {payingRoyalty && (
        <div
          className='royalty-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && closeRoyaltyPayment()
          }
        >
          <section
            className='royalty-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='pay-royalty-title'
          >
            <div className='royalty-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Royalty payment</span>
                <h2 id='pay-royalty-title'>Pay royalty</h2>
              </div>
              <button
                type='button'
                onClick={closeRoyaltyPayment}
                disabled={isPaying}
                aria-label='Close royalty payment form'
              >
                x
              </button>
            </div>
            <p className='royalty-payment-summary'>
              {getValue(payingRoyalty, ['author_name', 'author'])} | Payable:{' '}
              {formatCurrency(getPayableAmount(payingRoyalty))}
            </p>
            {paymentError && (
              <div className='royalty-page__error' role='alert'>
                {paymentError}
              </div>
            )}
            <form className='royalty-form' onSubmit={submitRoyaltyPayment}>
              <label>
                Payment date
                <input
                  name='paymentDate'
                  type='date'
                  value={paymentForm.paymentDate}
                  onChange={updatePaymentForm}
                  required
                />
              </label>
              <label>
                Amount
                <input
                  name='amount'
                  type='number'
                  min='0.01'
                  step='0.01'
                  value={paymentForm.amount}
                  onChange={updatePaymentForm}
                  required
                />
              </label>
              <label className='royalty-form__full'>
                Comment
                <textarea
                  name='comment'
                  rows='3'
                  value={paymentForm.comment}
                  onChange={updatePaymentForm}
                />
              </label>
              <div className='royalty-form__actions'>
                <button
                  className='royalty-button'
                  type='button'
                  onClick={closeRoyaltyPayment}
                  disabled={isPaying}
                >
                  Cancel
                </button>
                <button
                  className='royalty-button royalty-button--primary'
                  type='submit'
                  disabled={isPaying}
                >
                  {isPaying ? 'Saving...' : 'Pay royalty'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {royaltyToDelete && (
        <div
          className='royalty-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isDeleting &&
            setRoyaltyToDelete(null)
          }
        >
          <section
            className='royalty-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='delete-royalty-title'
          >
            <div className='royalty-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Confirm deletion</span>
                <h2 id='delete-royalty-title'>Delete royalty</h2>
              </div>
              <button
                type='button'
                onClick={() => setRoyaltyToDelete(null)}
                disabled={isDeleting}
                aria-label='Close delete royalty confirmation'
              >
                x
              </button>
            </div>
            <p className='royalty-payment-summary'>
              Delete royalty computation{' '}
              {getValue(royaltyToDelete, ['royaltyid', 'royalty_id', 'id'])}?
              This cannot be undone.
            </p>
            <div className='royalty-form__actions'>
              <button
                className='royalty-button'
                type='button'
                onClick={() => setRoyaltyToDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className='royalty-button royalty-actions__delete'
                type='button'
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete royalty'}
              </button>
            </div>
          </section>
        </div>
      )}

      {isModalOpen && (
        <div
          className='royalty-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && closeComputeModal()
          }
        >
          <section
            className='royalty-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='compute-royalty-title'
          >
            <div className='royalty-modal__header'>
              <div>
                <span className='app-content__eyebrow'>New calculation</span>
                <h2 id='compute-royalty-title'>Compute royalty</h2>
              </div>
              <button
                type='button'
                onClick={closeComputeModal}
                disabled={isComputing}
                aria-label='Close compute royalty form'
              >
                x
              </button>
            </div>
            {formError && (
              <div className='royalty-page__error' role='alert'>
                {formError}
              </div>
            )}
            <form className='royalty-form' onSubmit={submitCompute}>
              <label>
                Withholding tax (%)
                <input
                  name='withholdingTax'
                  type='number'
                  min='0'
                  max='100'
                  step='0.01'
                  value={form.withholdingTax}
                  onChange={updateForm}
                  required
                />
              </label>
              <label>
                Author
                <select
                  name='authorid'
                  value={form.authorid}
                  onChange={updateForm}
                  required
                >
                  <option value=''>Select author</option>
                  {authors.map((author) => (
                    <option
                      key={getAuthorId(author)}
                      value={getAuthorId(author)}
                    >
                      {getAuthorName(author)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Book
                <select
                  name='bookid'
                  value={form.bookid}
                  onChange={updateForm}
                  disabled={!form.authorid}
                  required
                >
                  <option value=''>Select book</option>
                  {availableBooks.map((book) => (
                    <option key={getBookId(book)} value={getBookId(book)}>
                      {getBookTitle(book)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                From date
                <input
                  name='fromDate'
                  type='date'
                  value={form.fromDate}
                  onChange={updateForm}
                  required
                />
              </label>
              <label>
                To date
                <input
                  name='toDate'
                  type='date'
                  value={form.toDate}
                  onChange={updateForm}
                  required
                />
              </label>
              <label className='royalty-form__full'>
                Comment
                <textarea
                  name='comment'
                  rows='4'
                  value={form.comment}
                  onChange={updateForm}
                  placeholder='Optional calculation note'
                />
              </label>
              <div className='royalty-form__actions'>
                <button
                  className='royalty-button'
                  type='button'
                  onClick={closeComputeModal}
                  disabled={isComputing}
                >
                  Cancel
                </button>
                <button
                  className='royalty-button royalty-button--primary'
                  type='submit'
                  disabled={isComputing}
                >
                  {isComputing ? 'Computing...' : 'Compute royalty'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  )
}

export default Royalty
